<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <div class="page-header-with-filters">
      <d-row
        no-gutters
        class="page-header py-4">
        <d-col
          col
          sm="6"
          class="text-center text-sm-left mb-4 mb-sm-0">
          <span class="text-uppercase page-subtitle">{{ $t('service.services') }}</span>
          <h3 class="page-title">{{ $t('service.group.servicesGroupsList') }}</h3>
        </d-col>
        <d-col
          col
          sm="6"
          class="text-right text-sm-right d-flex justify-content-end align-items-end">
          <d-button
            theme="success"
            @click.prevent="$router.push('/zdobienia/grupy/nowa')">
            <i class="material-icons">add</i>
            {{ $t('service.group.addNewGroup') }}
          </d-button>
        </d-col>
      </d-row>

      <list-filters
        :config="filtersConfig"
        :isListLoaded="loaded"
        :view-name="'services-groups'"
        @init="handleFiltersInit"
        @search="handleFiltersSearch" />
    </div>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0"
              style="width: 80px;">{{ $t('image.photo') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('service.group.groupName') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('service.group.groupCode') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('service.group.assortmentGroup') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('service.group.numberOfVariants') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('service.group.numberOfServices') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(group, index) of groups"
            :key="'group-' + index">
            <td class="align-middle">
              <img
                v-if="group.image_url"
                :src="group.image_url"
                alt=""
                class="group-image" />
              <span
                v-else
                class="group-empty-image">
                <i class="material-icons">camera_alt</i>
              </span>
            </td>
            <td class="align-middle">
              <d-badge
                v-if="group.is_package"
                theme="success"
                class="mr-2 mb-1">
                {{ $t('service.isPackage') }}
              </d-badge>
              <d-badge
                v-if="group.has_stitch_calculator"
                theme="info"
                class="mb-1">
                {{ $t('service.calculatorUse') }}
              </d-badge>
              <div>
                {{ group.name }}
              </div>
              <small>{{ $t('service.embroidery.type') }}:</small>
              <small
                v-if="group.embroidery_type_name"
                class="text-primary">
                {{ group.embroidery_type_name }}
              </small>
              <small
                v-else
                class="text-primary">
                &mdash;
              </small>

              <div v-if="group.has_stitch_calculator">
                <small>
                  {{ $t('service.calculatorType') }}:
                </small>
                <small
                  v-if="group.has_stitch_calculator && group.calculator_type"
                  class="text-primary">
                  {{ calculatorTypes[group.calculator_type] }}
                </small>
                <small
                  v-else
                  class="text-primary">
                  &mdash;
                </small>
              </div>
            </td>
            <td class="align-middle">
              {{ group.code }}
            </td>
            <td class="align-middle">
              {{ group.assortment_group }}
            </td>
            <td class="align-middle">
              <template v-if="group.services">{{ group.services.length }}</template>
              <template v-else>0</template>
            </td>
            <td class="align-middle">
              <template v-if="group.package_services">{{ group.package_services.length }}</template>
              <template v-else>0</template>
            </td>
            <td
              class="align-middle"
              style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/zdobienia/grupy/edycja/' + group.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeGroup(group.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="groups.length === 0">
            <td
              colspan="7"
              class="text-center">
              {{ $t('service.group.noServicesGroupsToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!loaded"
        class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import cloneDeep from 'lodash.clonedeep';

export default {
  name: 'services-groups',
  mixins: [
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    calculatorTypes () {
      return {
        'embroidery': this.$t('service.calculatorTypeEmbroidery'),
        'sublime': this.$t('service.calculatorTypeSublime'),
        'overprint': this.$t('service.calculatorTypeOverprint'),
        'custom': this.$t('service.calculatorTypeCustom')
      };
    },
    filtersConfig () {
      return [
        {
          key: 'search',
          apiKey: 'search',
          label: this.$t('ui.search'),
          placeholder: this.$t('service.group.enterServiceGroupNameOrCode'),
          type: 'text',
          isBasic: true,
          defaultValue: '',
          maxWidth: '400px'
        },
        {
          key: 'show_only_packages',
          apiKey: 'is_package',
          label: this.$t('service.group.showOnlyPackages'),
          type: 'checkbox',
          isBasic: true,
          defaultValue: false,
          apiTransform: (val, where) => {
            if (val) {
              where.is_package = 1;
            }
          }
        }
      ];
    }
  },
  data () {
    return {
      groups: [],
      loaded: false,
      loadError: false,
      removeInProgress: false,
      activeFilters: {},
      apiQueryPayload: {}
    };
  },
  methods: {
    handleFiltersInit (filters, apiPayload) {
      this.activeFilters = filters;
      this.apiQueryPayload = apiPayload;

      this.loadFilteredData();
    },
    handleFiltersSearch (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = cloneDeep(this.apiQueryPayload) || {};

      ListUtils.loadPaginatedItemsData(this, {
        method: 'get',
        endpoint: '/services/groups/items?page=' + this.currentPage,
        listField: 'groups',
        params: {
          with: ['services', 'packageServices'],
          where
        }
      });
    },
    removeGroup (groupID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/services/groups/delete/',
        id: groupID,
        successTitle: this.$t('service.group.removeServiceGroupSuccessTitle'),
        successText: this.$t('service.group.removeServiceGroupSuccessText'),
        confirmTitle: this.$t('service.group.removeServiceGroupConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('service.group.removeServiceGroupErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.group {
  &-image {
    height: auto;
    max-height: 80px;
    max-width: 80px;
    width: auto;
  }

  &-empty-image {
    align-items: center;
    background: #e5e5e5;
    color: #fff;
    display: flex;
    font-size: 48px;
    height: 80px;
    justify-content: center;
    width: 80px;
  }
}
</style>
